/**
 * BookingLinkPreviewButton — opens a fullscreen preview of the public
 * booking page rendered from the current unsaved booking-link create-form
 * values. Reuses the real public booking-page components (host card,
 * calendar, form) so the preview matches production pixel-for-pixel.
 *
 * No entity is created and no network write happens. Availability is
 * derived client-side from the form's working-hours value instead of
 * fetched, and month navigation on the calendar is suppressed (there's no
 * saved booking link to fetch availability for yet) — see
 * `buildIndicativeAvailability` and `handleCalendarClickCapture` below.
 */

import type { MouseEvent } from 'react';
import { useCallback, useMemo, useState } from 'react';

import { BookingCalendar, BookingForm } from '@/components/scheduling';
import { BookingErrorBoundary } from '@/components/scheduling/BookingErrorBoundary';
import { useDisplayTimezone } from '@/hooks/useDisplayTimezone';
import type {
  AvailabilityResponse,
  AvailableSlot,
  CustomQuestion,
  LocationType,
  PublicBookingType,
  SchedulingType,
} from '@/lib/api/types/scheduling';
import { Button } from '@/lib/base/buttons/button';
import { FullscreenModal } from '@/lib/base/overlays';
import { parseBookingQuestions } from '@/lib/crm/utils/bookingQuestions';
import type {
  DayOfWeek,
  WorkingHoursValue,
} from '@/lib/crm/utils/workingHours';
import { parseWorkingHours } from '@/lib/crm/utils/workingHours';
import { BookingHostCard } from '@/lib/scheduling/components/BookingHostCard';
import {
  dateFromComponentsInTimezone,
  getDatePartsInTimezone,
} from '@/lib/scheduling/timezone';

const DEFAULT_DURATION_MINUTES = 30;

// How many days ahead to synthesize indicative slots for. The calendar's
// month navigation is suppressed (see handleCalendarClickCapture), so this
// only needs to cover the initially-visible range, not a full month.
const INDICATIVE_DAYS = 14;

// Matches BookingLayout's `max-w-5xl` public booking page width, so the
// preview reads at the same scale as the real page.
const PREVIEW_MAX_CONTENT_WIDTH = 1024;

// Mirrors the real public booking page's content padding (BookingLayout.tsx
// uses `p-4 sm:p-6 md:p-8` around the same host-card/calendar/form
// components) so the preview reads with the same breathing room and the
// host card never touches the header divider.
const PREVIEW_CONTENT_PADDING = 'p-4 sm:p-6 md:p-8';

const DAY_INDEX_TO_KEY: readonly DayOfWeek[] = [
  'sunday',
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
];

function formatCalendarDate(year: number, month: number, day: number): string {
  return `${year.toString().padStart(4, '0')}-${month
    .toString()
    .padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
}

function toCustomQuestions(rawQuestions: unknown): CustomQuestion[] {
  return parseBookingQuestions(rawQuestions).map(q => ({
    key: q.key,
    label: q.label,
    type: q.type,
    required: q.required,
    options: q.options,
  }));
}

/**
 * Builds a synthetic PublicBookingType from the current (possibly
 * incomplete) create-form values, with fallbacks so the preview renders
 * even before required fields are filled in. Never sent anywhere — the
 * id/slug fields are harmless placeholders.
 */
function buildPreviewBookingType(
  values: Record<string, unknown>
): PublicBookingType {
  const name = String(values.ahoy_name ?? '').trim() || 'Untitled booking link';

  return {
    entity_id: 'preview',
    short_code: 'preview',
    name,
    display_name: name,
    description: String(values.ahoy_description ?? ''),
    duration_minutes:
      Number(values.ahoy_duration_minutes) || DEFAULT_DURATION_MINUTES,
    buffer_before_minutes: Number(values.ahoy_buffer_before_minutes) || 0,
    buffer_after_minutes: Number(values.ahoy_buffer_after_minutes) || 0,
    location_type: (values.ahoy_location_type as LocationType) ?? 'google_meet',
    owner_username: 'preview',
    slug: 'preview',
    global_slug: null,
    scheduling_type:
      (values.ahoy_scheduling_type as SchedulingType) ?? 'individual',
    custom_questions: toCustomQuestions(values.ahoy_custom_questions),
  };
}

/**
 * Derives indicative available slots from the form's working-hours value,
 * chopped into `durationMinutes` increments, for the next `INDICATIVE_DAYS`
 * days. This stands in for a real availability fetch — there is no saved
 * booking link yet to fetch availability for.
 *
 * Slot wall-clock times are generated *in the display timezone* (the host's
 * profile zone), not the browser's. The calendar renders each slot in the same
 * `timezone`, so a 9am–5pm working day shows at 9am–5pm regardless of where the
 * host happens to be viewing from — matching the configured working hours.
 */
function buildIndicativeAvailability(
  workingHours: WorkingHoursValue,
  durationMinutes: number,
  timezone: string
): AvailabilityResponse {
  const slots: AvailableSlot[] = [];
  const now = new Date();
  const durationMs = durationMinutes * 60_000;

  // "Today" is the calendar date in the display timezone, not the browser's.
  const today = getDatePartsInTimezone(now, timezone);

  for (let dayOffset = 0; dayOffset < INDICATIVE_DAYS; dayOffset++) {
    // A pure calendar counter: only UTC accessors are read from it, so it
    // carries no browser-timezone semantics — it just names consecutive
    // calendar days (Date.UTC handles month/year rollover).
    const dayUtc = new Date(
      Date.UTC(today.year, today.month - 1, today.day + dayOffset)
    );
    const dayConfig = workingHours[DAY_INDEX_TO_KEY[dayUtc.getUTCDay()]];
    if (!dayConfig.enabled) {
      continue;
    }

    const year = dayUtc.getUTCFullYear();
    const month = dayUtc.getUTCMonth() + 1;
    const day = dayUtc.getUTCDate();

    const [startHour, startMinute] = dayConfig.start.split(':').map(Number);
    const [endHour, endMinute] = dayConfig.end.split(':').map(Number);
    const dayEnd = dateFromComponentsInTimezone(
      year,
      month,
      day,
      endHour,
      endMinute,
      0,
      timezone
    );

    let slotStart = dateFromComponentsInTimezone(
      year,
      month,
      day,
      startHour,
      startMinute,
      0,
      timezone
    );

    while (slotStart.getTime() + durationMs <= dayEnd.getTime()) {
      const slotEnd = new Date(slotStart.getTime() + durationMs);
      if (slotStart.getTime() > now.getTime()) {
        slots.push({
          start: slotStart.toISOString(),
          end: slotEnd.toISOString(),
        });
      }
      slotStart = slotEnd;
    }
  }

  const endUtc = new Date(
    Date.UTC(today.year, today.month - 1, today.day + INDICATIVE_DAYS)
  );

  return {
    timezone,
    start_date: formatCalendarDate(today.year, today.month, today.day),
    end_date: formatCalendarDate(
      endUtc.getUTCFullYear(),
      endUtc.getUTCMonth() + 1,
      endUtc.getUTCDate()
    ),
    slots,
  };
}

interface BookingLinkPreviewContentProps {
  values: Record<string, unknown>;
}

function BookingLinkPreviewContent({ values }: BookingLinkPreviewContentProps) {
  const [step, setStep] = useState<'calendar' | 'form'>('calendar');
  const [selectedSlot, setSelectedSlot] = useState<AvailableSlot | null>(null);

  // The previewing host is an authenticated user: render the preview in their
  // profile timezone (backend always resolves one — never the browser's), so
  // the preview matches how they think about their own working hours.
  const timezone = useDisplayTimezone();

  const previewType = useMemo(() => buildPreviewBookingType(values), [values]);

  const indicativeAvailability = useMemo(
    () =>
      buildIndicativeAvailability(
        parseWorkingHours(values.ahoy_working_hours),
        previewType.duration_minutes,
        timezone
      ),
    [values.ahoy_working_hours, previewType.duration_minutes, timezone]
  );

  const handleSlotSelect = useCallback((slot: AvailableSlot) => {
    setSelectedSlot(slot);
    setStep('form');
  }, []);

  const handleBackToCalendar = useCallback(() => {
    setStep('calendar');
  }, []);

  // Preview only — never submit a real booking.
  const handleFormSubmit = useCallback(async () => {}, []);

  // The calendar fetches availability by slug when the visible month
  // changes (previous/next month nav, or clicking a leading/trailing day
  // from another month) — there's no saved booking link yet for that fetch
  // to target. Intercept those clicks in the capture phase before the
  // calendar's own handlers run; same-month day and slot selection (which
  // never trigger a fetch) are left interactive.
  const handleCalendarClickCapture = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      const target = event.target as HTMLElement;
      const label = target.closest('[aria-label]')?.getAttribute('aria-label');
      if (label && /month$/i.test(label)) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    []
  );

  // A crash here is recoverable — the create-form behind the modal is
  // untouched, so reset back to the calendar step rather than closing the
  // modal out from under the user.
  const handlePreviewError = useCallback(() => {
    setStep('calendar');
  }, []);

  return (
    <BookingErrorBoundary
      onReset={handlePreviewError}
      title="Couldn't render the preview"
      message='Something about these form values broke the preview. Try adjusting them and reopening the preview.'
    >
      <div className='grid grid-cols-1 gap-6 md:grid-cols-[260px_minmax(0,1fr)] lg:gap-10'>
        <BookingHostCard
          meetingType={previewType}
          timezone={timezone}
          className='md:border-r md:border-[var(--color-border-secondary)] md:pr-6 lg:pr-10'
        />
        {step === 'calendar' && (
          <div onClickCapture={handleCalendarClickCapture}>
            <BookingCalendar
              meetingTypeId='preview'
              isShortCode
              duration={previewType.duration_minutes}
              timezone={timezone}
              onSlotSelect={handleSlotSelect}
              initialAvailability={indicativeAvailability}
              // 'preview' is a placeholder short code with nothing behind it,
              // so any availability request 404s and surfaces "Meeting type
              // not found" over the calendar. initialAvailability alone does
              // not prevent that — see the prop's docs on BookingCalendar.
              disableAvailabilityFetch
            />
          </div>
        )}
        {step === 'form' && selectedSlot && (
          <BookingForm
            selectedSlot={selectedSlot}
            bookingType={previewType}
            timezone={timezone}
            customQuestions={previewType.custom_questions}
            onSubmit={handleFormSubmit}
            onBack={handleBackToCalendar}
          />
        )}
      </div>
    </BookingErrorBoundary>
  );
}

const PREVIEW_HEADER = (
  <p className='text-sm font-medium text-[var(--color-text-secondary)]'>
    Preview — no booking will be created
  </p>
);

interface BookingLinkPreviewButtonProps {
  values: Record<string, unknown>;
}

export function BookingLinkPreviewButton({
  values,
}: BookingLinkPreviewButtonProps) {
  const [isOpen, setIsOpen] = useState(false);

  const openPreview = useCallback(() => setIsOpen(true), []);
  const handleOpenChange = useCallback((next: boolean) => setIsOpen(next), []);

  return (
    <>
      <Button color='secondary' size='md' onClick={openPreview}>
        Preview
      </Button>
      <FullscreenModal
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        dialogAriaLabel='Booking link preview'
        instance='booking-link-preview'
        maxContentWidth={PREVIEW_MAX_CONTENT_WIDTH}
        header={PREVIEW_HEADER}
        contentClassName={PREVIEW_CONTENT_PADDING}
      >
        <BookingLinkPreviewContent values={values} />
      </FullscreenModal>
    </>
  );
}
